// packages/components/src/doctor-ai/ChatConsole.tsx
import React, { useRef, useEffect, useState } from 'react';
import { TextareaField, IconButton, ToggleSwitchField } from '@dopomogai/ui';
import { SendHorizonal, BrainCircuit, Languages, Paperclip } from 'lucide-react';
import { AssistantResponse } from './support-ai/AssistantResponse';

// --- Types for Reusability ---
interface BaseChatMessage {
  id: string;
  type: 'user' | 'assistant';
  text?: string;
}
interface UserMessage extends BaseChatMessage {
  type: 'user';
  text: string; // The original language from the user
  translatedText?: string; // The English translation
}
interface AssistantMessage extends BaseChatMessage {
  type: 'assistant';
  text?: string; // The English version for the customer
  originalLanguageText?: string; // The customer's language version
  isPrompt?: boolean;
  toolUsage?: any[];
  suggestedActions?: any[];
}
type AnyChatMessage = UserMessage | AssistantMessage;

interface ChatConsoleProps {
  chatHistory: AnyChatMessage[];
  onSendMessage: (message: string) => void;
  isSending: boolean;
  onActionClick: (actionId: string) => void;
  header?: React.ReactNode;
  chatMode: 'customer' | 'ai';
  onChatModeChange: (mode: 'customer' | 'ai') => void;
  onFileUpload: (files: File[]) => void;
}

// --- Sub-component for dual-language bubbles ---
const ChatMessageBubble: React.FC<{ message: UserMessage | AssistantMessage }> = ({ message }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const isUser = message.type === 'user';

  let originalText: string | undefined;
  let translatedText: string | undefined;

  if (isUser) {
    originalText = (message as UserMessage).text;
    translatedText = (message as UserMessage).translatedText;
  } else {
    originalText = (message as AssistantMessage).originalLanguageText;
    translatedText = (message as AssistantMessage).text;
  }

  const canTranslate = !!originalText && !!translatedText && originalText !== translatedText;
  const textToShow = showOriginal ? originalText : (translatedText || originalText);

  return (
    <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
      <div className={`rounded-xl p-4 max-w-xl shadow-md relative ${isUser ? 'bg-slate-100 text-slate-800 rounded-bl-none' : 'bg-emerald-600 text-white rounded-br-none'}`}>
        <p>{textToShow}</p>
        {canTranslate && (
          <div className="text-right mt-1">
            <button onClick={() => setShowOriginal(s => !s)} className={`flex items-center gap-1 text-xs font-semibold transition-opacity hover:opacity-100 ${isUser ? 'text-slate-500' : 'text-emerald-200 opacity-70'}`}>
              <Languages size={12} />
              {showOriginal ? 'Show Translation' : 'Show Original'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const ChatConsole: React.FC<ChatConsoleProps> = ({
  chatHistory,
  onSendMessage,
  isSending,
  onActionClick,
  header,
  chatMode,
  onChatModeChange,
  onFileUpload,
}) => {
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory, isSending]);

  const handleSend = () => {
    if (inputValue.trim() && !isSending) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      onFileUpload(Array.from(event.target.files));
      event.target.value = '';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {header}
      <div ref={scrollRef} className="flex-grow p-6 overflow-y-auto space-y-6">
        {chatHistory.map((item) => {
          if (item.type === 'user') {
            return <ChatMessageBubble key={item.id} message={item} />;
          }
          if (item.type === 'assistant') {
            if (item.isPrompt) {
              return <AssistantResponse key={item.id} responseText={item.text} />;
            }
            return <ChatMessageBubble key={item.id} message={item} />;
          }
          return null;
        })}
        {isSending && (
            <div className="flex justify-start">
                <div className="bg-slate-100 text-slate-800 rounded-xl rounded-bl-none p-4 max-w-lg shadow-md">
                    <div className="flex items-center gap-3 animate-pulse">
                        <BrainCircuit size={16} />
                        <span className="font-medium">AI is thinking...</span>
                    </div>
                </div>
            </div>
        )}
      </div>

      <div className="p-4 border-t-2 border-black bg-white flex-shrink-0 space-y-3">
        <div className="flex items-start gap-3">
          <input type="file" ref={fileInputRef} onChange={handleFileSelected} className="hidden" multiple />
          <IconButton icon={<Paperclip />} ariaLabel="Attach files" onClick={handleFileClick} variant="outline" disabled={isSending}/>
          <TextareaField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={chatMode === 'customer' ? 'Send a message to the customer...' : 'Ask the AI for help or suggestions...'}
            containerClassName="flex-grow"
            rows={1}
            disabled={isSending}
          />
          <IconButton
            icon={<SendHorizonal />}
            ariaLabel="Send message"
            onClick={handleSend}
            disabled={isSending || !inputValue.trim()}
            variant="primary"
          />
        </div>
        <div className="flex justify-end">
            <ToggleSwitchField
                label="Talk to AI"
                checked={chatMode === 'ai'}
                onChange={() => onChatModeChange(chatMode === 'customer' ? 'ai' : 'customer')}
                labelPosition="left"
            />
        </div>
      </div>
    </div>
  );
};